import { useEffect, useState } from 'react';
import { useHub, usePrivate } from '../context';
import { clanBanner, explainClanError, kick, loadClan, loadClanAdmin, loadClanDetails, loadClanExtras, loadClanItems, loadClanStats, platform, setRole, type BannerLayer, type Clan, type ClanMember } from '../clan';
import { REL_DAYS, REL_LONG_DAYS } from '../activity';
import { useRelationships } from '../shared';
import { img, sameOriginImg } from '../../lib/bungie';
import { Asking, Card, Check, Chip, Counting, Heading, Message, Pic, SideLabel, SmallButton, Split } from '../ui';

/** Port of the app's Clan tab: About, Roster, Admin, Relationships, Historical Stats and Exotics. */
export default function ClanTab({ sub }: { sub: string }) {
  const h = useHub();
  const { data: clan, error } = usePrivate('clan', () => loadClan(h.player!), { public: true });
  if (clan === undefined) return <Message title="Clan" text={error ?? 'Asking Bungie for your clan...'} />;
  if (clan === null) return <Message title="Clan" text="You are not in a clan." />;
  switch (sub) {
    case 'Roster': return <Roster clan={clan} />;
    case 'Admin': return <Admin clan={clan} />;
    case 'Relationships': return <Relationships clan={clan} />;
    case 'Historical Stats': return <Stats clan={clan} />;
    case 'Exotics': return <Exotics clan={clan} />;
    default: return <About clan={clan} />;
  }
}

// ---------------- About ----------------

function About({ clan }: { clan: Clan }) {
  const { data: extras } = usePrivate('clan-extras', () => loadClanExtras(clan), { public: true });
  const [banner, setBanner] = useState<BannerLayer[]>();
  useEffect(() => { clanBanner(clan).then(setBanner).catch(() => setBanner([])); }, [clan]);
  const part = clan.levelSize > 0 ? Math.min(1, clan.levelInto / clan.levelSize) : 1;
  return (
    <>
      <div className="g-clanabout">
        <SideLabel text="About" />
        <div className="g-stack" style={{ gap: 10, maxWidth: 900 }}>
          <div className="g-clanname">{clan.name}</div>
          {clan.motto && <p className="g-text2" style={{ fontSize: 15, fontStyle: 'italic' }}>{clan.motto}</p>}
          <div style={{ fontSize: 13.5 }}>// Founded {clan.founded?.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })} / {clan.memberCount} Members</div>
          {clan.about && <p className="g-text2" style={{ fontSize: 14, whiteSpace: 'pre-line' }}>{clan.about}</p>}
        </div>
        {banner && banner.length > 0 && (
          <div className="g-clanbanner" aria-label="Clan banner">
            {banner.map((l, i) => (
              <span key={i} className="layer" style={{ WebkitMaskImage: `url(${sameOriginImg(l.path)})`, maskImage: `url(${sameOriginImg(l.path)})` }}>
                <span style={{ background: l.color }} />
                <img src={img(l.path)} alt="" />
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="g-clanabout" style={{ marginTop: 24 }}>
        <SideLabel text="Progression" />
        <div className="g-stack" style={{ gap: 10, maxWidth: 700 }}>
          <div className="g-clanlevel">
            <span className="fill" style={{ width: `${part * 100}%` }} />
            <span>Level {clan.level}</span>
            <span>{clan.levelSize > 0 ? `${clan.levelInto.toLocaleString()}/${clan.levelSize.toLocaleString()}` : 'Maximum level'}</span>
          </div>
          <p className="g-text2" style={{ fontSize: 13, fontStyle: 'italic' }}>As your clan levels up, clan banner perks are unlocked for all members. Complete activities throughout the system to increase your clan's level.</p>
          {clan.weeklyXp > 0 && <p className="g-text2" style={{ fontSize: 13 }}>Earned this week: {clan.weeklyXp.toLocaleString()} XP</p>}
          {extras && extras.weekly.length > 0 && (
            <div className="g-wrap" style={{ gap: 8, marginTop: 8 }}>
              {extras.weekly.map((w) => (
                <span key={w.name} className={`g-engram ${w.earned ? 'on' : ''}`} title={w.earned ? `${w.name}: earned this week, every member can collect the clan engram` : `${w.name}: no member has earned this week's clan engram yet`}>
                  {w.earned ? '✓' : '○'} {w.name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      {extras && extras.perks.length > 0 && (
        <div className="g-clanabout" style={{ marginTop: 50 }}>
          <SideLabel text="Banner perks" />
          <div className="g-wrap" style={{ gap: 8, maxWidth: 1100 }}>
            {extras.perks.map((p) => {
              const has = clan.level >= p.level;
              return (
                <Card key={p.name + p.level} className="g-perkcard">
                  <Pic src={p.icon} width={44} height={44} style={{ opacity: has ? 1 : 0.4 }} />
                  <div>
                    <b style={{ color: has ? '#fff' : 'var(--text3)' }}>{p.name}</b>
                    <div className="small" style={{ color: has ? 'var(--gold)' : 'var(--text3)' }}>{has ? `Level ${p.level}` : `Unlocks at level ${p.level}`}</div>
                    {p.desc && <p className="small" style={{ margin: 0, color: has ? 'var(--text2)' : '#8c95a3' }}>{p.desc}</p>}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

// ---------------- Roster ----------------

const since = (from?: Date) => {
  if (!from) return '';
  const now = new Date();
  const months = (now.getFullYear() - from.getFullYear()) * 12 + now.getMonth() - from.getMonth() - (now.getDate() < from.getDate() ? 1 : 0);
  if (months >= 12) return `${Math.floor(months / 12)} yr` + (months % 12 ? `, ${months % 12} mth${months % 12 === 1 ? '' : 's'}` : '');
  if (months > 0) return `${months} mth${months === 1 ? '' : 's'}`;
  return `${Math.max(0, Math.floor((now.getTime() - from.getTime()) / 86_400_000))} days`;
};

function Roster({ clan }: { clan: Clan }) {
  const h = useHub();
  const { data: full } = usePrivate('clan-details', () => loadClanDetails(clan, h.season?.rewardProgression ?? 0), { public: true });
  const members = full?.members ?? clan.members;
  const online = members.filter((m) => m.online).length;
  const sorted = [...members].sort((a, b) => Number(b.online) - Number(a.online) || ((b.lastPlayed ?? b.lastOnline)?.getTime() ?? 0) - ((a.lastPlayed ?? a.lastOnline)?.getTime() ?? 0));
  return (
    <>
      <p className="g-text2" style={{ textAlign: 'right', margin: 0 }}>{online} Online{full ? '' : '   (asking Bungie for each member\'s character...)'}</p>
      <div className="g-stack" style={{ gap: 3 }}>
        <div className="g-roster head"><span /><span>MEMBER</span><span>PLATFORM</span><span>CLASS</span><span>POWER</span><span>RANK</span><span>SEASON</span><span>ACTIVITY</span><span>JOINED</span><span>ROLE</span></div>
        {sorted.map((m) => {
          const t = m.lastPlayed ?? m.lastOnline;
          const days = t ? (Date.now() - t.getTime()) / 86_400_000 : 0;
          let last = m.online ? 'Online now' : !t ? '-' : days < 1 ? 'Today' : days < 60 ? (Math.floor(days) === 1 ? 'Yesterday' : `${Math.floor(days)} days ago`) : t.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
          if (m.online && m.activity) last = m.activity;
          return (
            <div key={m.memberId} className={`g-roster ${m.online ? 'online' : ''}`} title={`${m.name}\nJoined ${m.joined?.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }) ?? ''}${m.lastPlayed ? `\nLast played ${m.lastPlayed.toLocaleString()}` : ''}`}>
              <span>{m.emblem && <Pic src={m.emblem} width={34} height={34} />}</span>
              <span className="name">{m.name}</span><span>{platform(m.membershipType)}</span><span>{m.cls ?? ''}</span>
              <span className="gold">{m.light || ''}</span><span>{m.guardianRank || ''}</span><span>{m.seasonRank || ''}</span>
              <span className={m.online ? 'good' : ''}>{last}</span><span>{since(m.joined)}</span><span>{m.role}</span>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ---------------- Admin ----------------

function Admin({ clan }: { clan: Clan }) {
  const h = useHub();
  const { data, error, again } = usePrivate(`clan-admin:${clan.groupId}`, (t) => loadClanAdmin(t, clan.groupId));
  const [note, setNote] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<string>();
  const act = async (done: string, work: (t: string) => Promise<unknown>) => {
    setBusy(true); setNote(undefined);
    try { const t = await h.getToken(); await work(t!); setNote(`Done: ${done}`); h.touch(); }
    catch (e) { setNote(`Bungie did not allow it: ${explainClanError(e)}`); }
    finally { setBusy(false); setConfirm(undefined); }
  };
  const section = (title: string, none: string, people: { name: string; note: string; when?: Date }[]) => (
    <div key={title} className="g-stack" style={{ gap: 6 }}>
      <Heading>{`${title} (${people.length})`.toUpperCase().split('').join(' ')}</Heading>
      {people.length === 0 ? <p className="g-dim">{none}</p> : people.map((p, i) => (
        <Card key={i} className="g-adminrow"><div><b>{p.name}</b>{p.note && <p className="g-text2 small">{p.note}</p>}</div>{p.when && <span className="g-dim small">{p.when.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</span>}</Card>
      ))}
    </div>
  );
  const roleOrder = (m: ClanMember) => (m.role === 'Founder' || m.role === 'Acting founder' ? 0 : m.role === 'Admin' ? 1 : 2);
  const isMe = (m: ClanMember) => m.memberId === h.player?.membershipId || !!m.also?.some((a) => a.memberId === h.player?.membershipId);
  // Bungie's ranks: only the founder promotes, demotes and hands the clan over; an admin can only kick members below
  // admin. The buttons follow the signed-in player's own rank, so nobody is offered what Bungie will refuse.
  const myRole = h.own ? clan.members.find(isMe)?.role : undefined;
  const iAmFounder = myRole === 'Founder' || myRole === 'Acting founder';
  const iAmAdmin = myRole === 'Admin';
  // Bungie keeps the founder handover for its own site and apps (AbdicateFoundership wants PrivilegedScope, which no
  // other site can have), so the founder is sent to the clan's page there: Bungie's own old member-admin address forwards here
  const bungieClanAdmin = `https://www.bungie.net/7/en/Clans/MyClans?groupId=${clan.groupId}`;
  const ask = (what: string, m: ClanMember, text: string, yes: string, done: string, work: (t: string) => Promise<unknown>) => (confirm === `${what}:${m.memberId}`
    ? <><span className="small">{text}</span><button type="button" className="btn primary small" disabled={busy} onClick={() => act(done, work)}>{yes}</button><SmallButton onClick={() => setConfirm(undefined)}>Cancel</SmallButton></>
    : null);
  return (
    <Split
      left={(
        <div className="g-stack" style={{ gap: 10 }}>
          <h1 className="g-title">Admin</h1>
          <p className="g-text2 big14">Who is waiting on {clan.name}'s admins. Bungie shows these lists only to a signed-in founder or admin of the clan.</p>
          <p className="g-dim small">Under the lists: every member, with the buttons your rank allows. The founder can Promote (to admin), Demote and Kick; an admin can kick members below admin. Accepting, inviting and banning are done in the game or on bungie.net.</p>
          <p className="g-dim small">Handing the clan over: Bungie allows this only on its own site and app, so it can't be done here. The new founder must be an admin first (Promote them here). Then the founder clicks Hand over on bungie.net beside them, opens the clan's Members there and clicks Set as Founder next to that admin. It can't be undone. A founder can't leave a clan until it is handed over; after that, leave in the game, or ask the new founder to kick you here.</p>
          {note && <p className={note.startsWith('Done') ? 'g-good' : 'g-warn'} style={{ fontSize: 13 }}>{note}</p>}
          <div><SmallButton onClick={again}>Read again</SmallButton></div>
        </div>
      )}
      right={!h.own ? <p className="g-text2">Sign in with Bungie (top right) to see the clan's admin lists.</p> : (
        <div className="g-stack" style={{ gap: 10 }}>
          {!data ? <div className="g-clanerr"><Asking text={error ? (/application scope/i.test(String(error)) ? `Bungie did not give out the admin lists: ${explainClanError(error)}` : `Bungie did not give out the admin lists: ${error} The lists are only for the clan's founder and admins.`) : "Asking Bungie for the clan's admin lists..."} /></div> : (
            <>
              {section('Asked to join', 'Nobody is waiting to be let in.', data.pending)}
              {section('Invited', 'No invitations are open.', data.invited)}
              {section('Banned', 'Nobody is banned.', data.banned)}
            </>
          )}
          <Heading>{`Members (${clan.members.length})`.toUpperCase().split('').join(' ')}</Heading>
          {[...clan.members].sort((a, b) => roleOrder(a) - roleOrder(b) || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })).map((m) => {
            const me = isMe(m);
            const boss = m.role === 'Founder' || m.role === 'Acting founder';
            const asking = ask('kick', m, `Remove ${m.name}?`, 'Yes, remove', `${m.name} was removed from the clan.`, (t) => kick(t, clan.groupId, m));
            return (
              <div key={m.memberId} className="g-adminmember">
                <span className="n">{m.name}</span>
                <span style={{ color: m.role === 'Member' ? 'var(--text2)' : 'var(--gold)' }}>{m.role}</span>
                <span className="acts">
                  {asking ?? (
                    <>
                      {iAmFounder && (m.role === 'Member' || m.role === 'Beginner') && !me && <SmallButton disabled={busy} onClick={() => act(`${m.name} is now an admin.`, (t) => setRole(t, clan.groupId, m, 3))}>Promote</SmallButton>}
                      {iAmFounder && m.role === 'Admin' && !me && <SmallButton disabled={busy} onClick={() => act(`${m.name} is now a member.`, (t) => setRole(t, clan.groupId, m, 2))}>Demote</SmallButton>}
                      {iAmFounder && m.role === 'Admin' && !me && <a className="g-small" href={bungieClanAdmin} target="_blank" rel="noopener noreferrer" title={`Opens ${clan.name} on bungie.net: under Members, click Set as Founder next to ${m.name}. It can't be undone.`}>Hand over on bungie.net ↗</a>}
                      {(iAmFounder || (iAmAdmin && m.role !== 'Admin')) && !boss && !me && <SmallButton disabled={busy} onClick={() => setConfirm(`kick:${m.memberId}`)}>Kick</SmallButton>}
                    </>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    />
  );
}

// ---------------- Relationships ----------------

function Relationships({ clan }: { clan: Clan }) {
  const h = useHub();
  const [days, setDays] = useState(REL_DAYS);
  const [clanOnly, setClanOnly] = useState(true);
  const rel = useRelationships(days);
  if (!rel.data) {
    return (
      <Message title="Relationships" text={rel.error ?? `Analysing your activities of the last ${days} days. The first time takes a minute or two: each match report is downloaded once, then kept in this browser.`}>
        {rel.error ? <SmallButton onClick={rel.again}>Try again</SmallButton> : <p style={{ fontSize: 13.5 }}>{rel.progress[1] ? `Downloading reports  ${rel.progress[0].toLocaleString()} / ${rel.progress[1].toLocaleString()}` : 'Collecting your activity history from Bungie...'}</p>}
      </Message>
    );
  }
  // a clan member is recognised by account id, or by Bungie name (a cross-save account can carry another id in reports)
  const inClan = new Set([...clan.members.map((m) => m.memberId), ...clan.members.filter((m) => m.name.includes('#')).map((m) => m.name.toLowerCase())]);
  const people = rel.data.people.filter((p) => !clanOnly || inClan.has(p.memberId) || inClan.has(p.name.toLowerCase()));
  const met = new Set(rel.data.people.flatMap((p) => [p.memberId, p.name.toLowerCase()]));
  const strangers = clan.members.filter((m) => !met.has(m.memberId) && !met.has(m.name.toLowerCase()) && m.memberId !== h.player?.membershipId).sort((a, b) => a.name.localeCompare(b.name));
  const top = people.slice(0, 20);
  const pairs = people.filter((p) => p.duos >= 3).sort((a, b) => b.duos - a.duos).slice(0, 10);
  const person = (name: string, count: string, tip: string, pink = false) => (
    <div key={name + count} className={`g-person ${pink ? 'pink' : ''}`} title={tip}><b>{count}</b><span>{name}</span></div>
  );
  return (
    <div className="g-rel">
      <div className="g-stack" style={{ gap: 10 }}>
        <h1 className="g-title">Relationships</h1>
        <p className="g-text2 big14">Discover missed opportunities and secret lovers. These Guardians are those who you play most activities with. Cherish each other.</p>
        <Check label="Clan members only" on={clanOnly} set={setClanOnly} />
        <div className="g-wrap" style={{ gap: 28, marginTop: 6 }}>
          {[[people.reduce((s, p) => s + p.activities, 0), 'Encounters'], [people.reduce((s, p) => s + p.fireteams, 0), 'Fireteams'], [strangers.length, 'Undiscovered Friends']].map(([v, l]) => (
            <div key={l as string}><div style={{ fontSize: 22 }}><Counting value={v as number} /></div><div className="g-dim small">{l}</div></div>
          ))}
        </div>
        <p className="g-dim small">From {rel.data.reports.toLocaleString()} activities in the last {rel.data.days} days, all your characters together.</p>
        <div className="g-tools">
          <SmallButton onClick={rel.again}>Read again</SmallButton>
          <SmallButton onClick={() => setDays(rel.data!.days > REL_DAYS ? REL_DAYS : REL_LONG_DAYS)}>{rel.data.days > REL_DAYS ? `Only the last ${REL_DAYS} days` : 'Look back a whole year'}</SmallButton>
        </div>
      </div>
      <div className="g-stack" style={{ gap: 3 }}>
        <div className="g-caps2">BEST FRIENDS</div>
        <div className="g-dim small">{top.length ? `Top ${top.length} by activities played` : ''}</div>
        <div className="g-rule" style={{ margin: '2px 0 6px' }} />
        {top.length === 0 ? <p className="g-text2">{clanOnly ? 'You have not played with a clan member in this time. Untick "Clan members only" to see everyone.' : 'No shared activities found in this time.'}</p>
          : top.map((p) => person(p.name || '(no name)', String(p.activities), `${p.name}\n${p.activities} activities together, ${p.fireteams} of them in your fireteam, ${p.duos} as a pair`))}
        <div className="g-caps2" style={{ marginTop: 22 }}>ROMANTIC PARTNERSHIPS</div>
        <div className="g-dim small">Guardians you went out with as a fireteam of two, three times or more</div>
        <div className="g-rule" style={{ margin: '2px 0 6px' }} />
        {pairs.length === 0 ? <p className="g-text2" style={{ fontStyle: 'italic' }}>No one said dating was easy. Back to the fight, Guardian!</p>
          : pairs.map((p) => person(p.name, String(p.duos), `${p.name}\n${p.duos} activities as a fireteam of two`, true))}
      </div>
      <div className="g-stack" style={{ gap: 3 }}>
        <div className="g-caps2">UNDISCOVERED FRIENDS</div>
        <div className="g-dim small">{strangers.length} clan member{strangers.length === 1 ? '' : 's'} you have not played with in this time</div>
        <div className="g-rule" style={{ margin: '2px 0 6px' }} />
        {strangers.length === 0 ? <p className="g-text2">You have played with everyone in your clan.</p>
          : strangers.map((m) => person(m.name, m.online ? '●' : '', m.online ? `${m.name}\nOnline right now` : m.name))}
      </div>
    </div>
  );
}

// ---------------- Historical Stats ----------------

const PVE_COLS: [string, string][] = [['kills', 'KILLS'], ['deaths', 'DEATHS'], ['assists', 'ASSISTS'], ['killsDeathsRatio', 'K/D'], ['precisionKills', 'PRECISION'], ['activitiesEntered', 'ACTIVITIES'], ['activitiesCleared', 'CLEARED'], ['bestSingleGameKills', 'BEST GAME'], ['longestKillSpree', 'BEST STREAK'], ['secondsPlayed', 'TIME']];
const PVP_COLS: [string, string][] = [['kills', 'KILLS'], ['deaths', 'DEATHS'], ['assists', 'ASSISTS'], ['killsDeathsRatio', 'K/D'], ['efficiency', 'EFFICIENCY'], ['activitiesEntered', 'MATCHES'], ['activitiesWon', 'WINS'], ['bestSingleGameKills', 'BEST GAME'], ['longestKillSpree', 'BEST STREAK'], ['secondsPlayed', 'TIME']];

function Stats({ clan }: { clan: Clan }) {
  const { data: full } = usePrivate('clan-stats', () => loadClanStats(clan), { public: true });
  const [pvp, setPvp] = useState(false);
  const cols = pvp ? PVP_COLS : PVE_COLS;
  const of = (m: ClanMember) => (pvp ? m.pvp : m.pve) ?? {};
  return (
    <>
      <div className="g-tools" style={{ marginBottom: 14, gap: 14 }}>
        <select className="g-input g-select big" style={{ width: 260, marginTop: 0 }} value={pvp ? 1 : 0} onChange={(e) => setPvp(e.target.value === '1')} aria-label="Stats for">
          <option value={0}>Vanguard and PvE</option><option value={1}>Crucible</option>
        </select>
        <span className="g-text2" style={{ fontSize: 13 }}>{full ? 'All-time totals for each member\'s whole account. Sorted by kills.' : 'Asking Bungie for each member\'s totals...'}</span>
      </div>
      {full && (
        <div className="g-stack" style={{ gap: 3 }}>
          <div className="g-statstable head"><span>MEMBER</span>{cols.map(([, l]) => <span key={l}>{l}</span>)}</div>
          {[...full.members].sort((a, b) => (of(b).kills?.value ?? 0) - (of(a).kills?.value ?? 0)).map((m) => {
            const s = of(m);
            return (
              <div key={m.memberId} className="g-statstable" style={{ opacity: Object.keys(s).length ? 1 : 0.5 }}>
                <span className="name">{m.name}</span>
                {cols.map(([k], i) => {
                  const v = s[k];
                  const text = !v ? '-' : k === 'secondsPlayed' ? `${Math.floor(v.value / 3600).toLocaleString()} h` : v.value >= 1000 ? Math.round(v.value).toLocaleString() : v.text;
                  return <span key={k} className={i === 0 ? 'gold' : ''}>{text}</span>;
                })}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ---------------- Exotics: who owns what ----------------

function Exotics({ clan }: { clan: Clan }) {
  const h = useHub();
  const defs = h.defs;
  const { data: full } = usePrivate('clan-items', () => loadClanItems(clan), { public: true });
  const [alpha, setAlpha] = useState(false);
  const [bySource, setBySource] = useState(false);
  if (!defs) return <Message title="Exotics" text="The item list is still being read." />;
  if (!full) return <Message title="Exotics" text="Asking Bungie what each member has collected..." />;
  const open = full.members.filter((m) => m.owned);
  const hidden = full.members.length - open.length;
  const exotic = (defs.nodes.get(defs.roots.collections)?.nodes ?? []).find((n) => defs.nodes.get(n)?.name.toLowerCase().startsWith('exotic'));
  const under = (node: number): number[] => { const d = defs.nodes.get(node); return d ? [...d.collectibles, ...d.nodes.flatMap(under)] : []; };
  const section = (title: string, all: number[]) => {
    const list = [...new Set(all)].filter((c) => defs.collectibles.has(c));
    if (!list.length) return null;
    const owners = (c: number) => open.filter((m) => m.owned!.has(c)).map((m) => m.name);
    const sorted = alpha ? list.sort((a, b) => defs.collectibles.get(a)!.name.localeCompare(defs.collectibles.get(b)!.name)) : list.sort((a, b) => owners(b).length - owners(a).length);
    return (
      <div key={title}>
        <Heading style={{ margin: '26px 0 10px' }}>{title.toUpperCase().split('').join(' ')}</Heading>
        <div className="g-wrap g6">
          {sorted.map((c) => {
            const who = owners(c);
            const def = defs.collectibles.get(c)!;
            return (
              <span key={c} className={`g-clanexo ${who.length === open.length ? 'all' : ''}`} title={`${def.name}\n${who.length} of ${open.length} members${who.length ? '\n\n' + who.join('\n') : ''}`}>
                <Pic src={def.icon} style={{ opacity: who.length ? 1 : 0.28 }} lazy />
                <b>{who.length}</b>
              </span>
            );
          })}
        </div>
      </div>
    );
  };
  const from = (s: string) => /raid/i.test(s) ? 'Raids' : /dungeon/i.test(s) ? 'Dungeons' : /exotic mission|quest|mission/i.test(s) ? 'Quests and missions'
    : /season pass|season of|episode/i.test(s) ? 'Seasons' : /engram|xûr|world/i.test(s) || !s ? 'World drops' : 'Other sources';
  const order = ['Raids', 'Dungeons', 'Quests and missions', 'Seasons', 'World drops', 'Other sources'];
  return (
    <>
      <p className="g-text2" style={{ fontSize: 13.5 }}>Who in the clan owns which exotic. The number on each one is how many of {open.length} members have it; point at it for the names.{hidden > 0 ? ` ${hidden} member${hidden === 1 ? ' keeps' : 's keep'} their collection private.` : ''}</p>
      {!exotic || !open.length ? <p className="g-text2">Nothing to show.</p> : (
        <>
          <div className="g-chips">
            <Chip on={!alpha} onClick={() => setAlpha(false)}>Most owned first</Chip>
            <Chip on={alpha} onClick={() => setAlpha(true)}>Alphabetical</Chip>
            <span style={{ width: 14 }} />
            <Chip on={!bySource} onClick={() => setBySource(false)}>By kind</Chip>
            <Chip on={bySource} onClick={() => setBySource(true)}>By where it comes from</Chip>
          </div>
          {bySource
            ? order.map((g) => section(g, under(exotic).filter((c) => defs.collectibles.has(c) && from(defs.collectibles.get(c)!.source) === g)))
            : [...defs.nodes.get(exotic)!.nodes.map((k) => section(defs.nodes.get(k)?.name ?? '', under(k))), section('Exotic', defs.nodes.get(exotic)!.collectibles)]}
        </>
      )}
    </>
  );
}
